// Cross-field relationship rules for site estimates against global estimates.
//
// These are blocking validations. The values themselves keep saving (the proposal autosaves as a
// draft) and the same rules are applied by the API site validation, which is what stops an invalid
// assessment from being proposed. The helpers here exist so the form can show the error as soon as
// either side of a relationship changes, instead of waiting for Propose.

export const BEST_SITE_EXCEEDS_BEST_GLOBAL_MESSAGE =
  'Your best site estimate should not exceed the best global estimate'
export const BEST_SITE_EXCEEDS_GLOBAL_EXTENT_MESSAGE =
  'Your best site estimate should not exceed the global estimate'
export const SITE_EXTENT_ORDER_MESSAGE =
  'Minimum should be less than best, minimum and best should be less than maximum'

export const EMPTY_FIELD_ERROR = { field: '', error: '' }

// Only blank values count as "not provided", so a numeric 0 still takes part in a comparison.
// Values arrive as strings from the inputs, so everything is compared as a number rather than
// lexicographically. Booleans are rejected outright: some of these props are declared as
// oneOfType([number, string, bool]), and Number(true) would otherwise compare as 1.
export const isComparableNumber = (value) => {
  if (typeof value === 'boolean' || value === null || value === undefined) {
    return false
  }

  // Whitespace-only is as good as empty; without this Number('  ') would compare as 0.
  if (String(value).trim() === '') {
    return false
  }

  return Number.isFinite(Number(value))
}

const bothComparable = (first, second) => {
  return isComparableNumber(first) && isComparableNumber(second)
}

// Ecosystem extents only count as provided when above zero. Clearing an optional bound stores 0
// rather than null, and 0 is not a meaningful area in any case, so a blank or zeroed min/max must
// not drag the ordering down — a best-only ecosystem is valid.
export const isProvidedExtent = (value) => {
  return isComparableNumber(value) && Number(value) > 0
}

const bothExtentsProvided = (first, second) => {
  return isProvidedExtent(first) && isProvidedExtent(second)
}

// bestSite must stay below or equal to bestGlobal. Nothing to compare until both values are provided.
export const validateBestSiteAgainstBestGlobal = (bestSite, bestGlobal, fields) => {
  if (!bothComparable(bestSite, bestGlobal) || Number(bestSite) <= Number(bestGlobal)) {
    return {}
  }

  return {
    [fields.bestSite]: BEST_SITE_EXCEEDS_BEST_GLOBAL_MESSAGE,
    [fields.bestGlobal]: BEST_SITE_EXCEEDS_BEST_GLOBAL_MESSAGE
  }
}

// The ecosystem best extent at site must stay below or equal to the global extent.
export const validateBestSiteAgainstGlobalExtent = (bestSite, globalExtent, fields) => {
  if (!bothExtentsProvided(bestSite, globalExtent) || Number(bestSite) <= Number(globalExtent)) {
    return {}
  }

  return {
    [fields.bestSite]: BEST_SITE_EXCEEDS_GLOBAL_EXTENT_MESSAGE,
    [fields.globalExtent]: BEST_SITE_EXCEEDS_GLOBAL_EXTENT_MESSAGE
  }
}

// min <= best <= max, evaluated pair by pair so a single value on its own is never reported as out of
// order. A broken ordering is reported against all three fields.
export const isSiteExtentOrderValid = (min, best, max) => {
  const pairs = [
    [min, best],
    [best, max],
    [min, max]
  ]

  return pairs.every(([lower, higher]) => {
    if (!bothExtentsProvided(lower, higher)) {
      return true
    }

    return Number(lower) <= Number(higher)
  })
}

export const isSiteEstimateOrderValid = isSiteExtentOrderValid

export const SITE_ESTIMATE_ORDER_MESSAGE = SITE_EXTENT_ORDER_MESSAGE

export const validateSiteExtentOrder = (min, best, max, fields) => {
  if (isSiteExtentOrderValid(min, best, max)) {
    return {}
  }

  return {
    [fields.min]: SITE_EXTENT_ORDER_MESSAGE,
    [fields.best]: SITE_EXTENT_ORDER_MESSAGE,
    [fields.max]: SITE_EXTENT_ORDER_MESSAGE
  }
}

export const validateSiteEstimateOrder = validateSiteExtentOrder

// Turns the field -> message map into the { field, error } shape the Input component renders.
export const getRelationshipFieldError = (relationshipErrors, field) => {
  const message = relationshipErrors?.[field]

  return message ? { field, error: message } : EMPTY_FIELD_ERROR
}

// A relationship error takes precedence over the "please complete this field" style errors derived
// from the API validation, which cannot explain why the combination of values is wrong.
export const withRelationshipError = (relationshipErrors, field, fallbackError) => {
  const relationshipError = getRelationshipFieldError(relationshipErrors, field)

  return relationshipError.field === field ? relationshipError : fallbackError
}
